// client/src/components/public/AddToCalendarMenu.jsx
//
// "Add to calendar" disclosure menu for the public event page (Growth Phase
// 7A). Every URL comes from the server (GET /api/public/events/... →
// `calendar`): Google and Outlook.com templates, the event .ics (Apple
// Calendar, Outlook desktop, anything else), and the tenant's subscribable
// schedule feed. The server builds them all from utils/ics.js, so the
// links and the .ics agree on times, location and UID.

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** https://host/x.ics → webcal://host/x.ics (opens the OS calendar's subscribe flow). */
export function toWebcal(url) {
  return url ? String(url).replace(/^https?:\/\//i, 'webcal://') : null;
}

export default function AddToCalendarMenu({ calendar, tenantName }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!calendar || !(calendar.google || calendar.outlook || calendar.ics)) return null;

  const items = [
    calendar.google && { key: 'google', href: calendar.google, label: t('addToCalendar.google'), external: true },
    calendar.outlook && { key: 'outlook', href: calendar.outlook, label: t('addToCalendar.outlook'), external: true },
    calendar.ics && { key: 'ics', href: calendar.ics, label: t('addToCalendar.ics'), download: true },
    calendar.feed && {
      key: 'feed',
      href: toWebcal(calendar.feed),
      label: tenantName
        ? t('addToCalendar.subscribeNamed', { name: tenantName })
        : t('addToCalendar.subscribe'),
    },
  ].filter(Boolean);

  return (
    <div ref={rootRef} className="os-cal-menu" style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls="os-cal-menu-list"
        onClick={() => setOpen((v) => !v)}
        style={trigger}
      >
        {t('addToCalendar.button')} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul id="os-cal-menu-list" style={panel}>
          {items.map((it) => (
            <li key={it.key}>
              <a
                href={it.href}
                style={itemStyle}
                onClick={() => setOpen(false)}
                {...(it.external ? { target: '_blank', rel: 'noreferrer' } : {})}
                {...(it.download ? { download: '' } : {})}
              >
                {it.label}
              </a>
            </li>
          ))}
          {calendar.feed && (
            <li className="os-muted" style={hint}>{t('addToCalendar.subscribeHint')}</li>
          )}
        </ul>
      )}
    </div>
  );
}

const trigger = {
  border: '1px solid #d1d5db', background: '#ffffff', color: '#374151', padding: '10px 16px',
  borderRadius: 8, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
};
const panel = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 20, minWidth: 260,
  listStyle: 'none', margin: 0, padding: 6, background: '#ffffff',
  border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
};
const itemStyle = {
  display: 'block', padding: '9px 12px', borderRadius: 6, fontSize: 14, textDecoration: 'none',
};
const hint = { padding: '6px 12px 4px', fontSize: 12, lineHeight: 1.4, borderTop: '1px solid #e5e7eb', marginTop: 4 };
